// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import GlobalStyles from "@mui/material/GlobalStyles";
import Container from "@mui/material/Container";
import { Paper } from "@mui/material";
import Banner from "../assets/img/certification-banner-updated-2026.png";
import CertificationVerifier from "../components/landing-page/CertificationVerifier";
import GuideCards from "../components/landing-page/GuideCards";

import { RootState, useAppSelector } from "../slices/store";
import { BANNER_TOGGLE_EVENT, LOCAL_STORAGE_KEYS } from "../config/constant";
import Loading from "./Loading";
import WelcomeCard from "../components/landing-page/WelcomeCard";
import NotificationBannerCard from "../components/landing-page/NotificationBannerCard";
import { BannerState } from "../utils/types";
import PublicNotificationBannerCard from "../components/landing-page/PublicNotificationBannerCard";
import BannerFabController from "../components/landing-page/BannerFabController";

function LandingPage() {
  const auth = useAppSelector((state: RootState) => state.auth);
  const contactState = useAppSelector((state: RootState) => state.contact);
  const appMaintenanceStatus = useAppSelector((state: RootState) => state.app.response);

  // Track localStorage state reactively to handle updates
  const [localStorageState, setLocalStorageState] = useState({
    welcomeHidden: false,
    notificationHidden: false
  });

  // Read localStorage on mount and listen for banner-toggle events
  useEffect(() => {
    const bannerId = LOCAL_STORAGE_KEYS.NOTIFICATION_BANNER_DISMISSED;
    const welcomeKey = LOCAL_STORAGE_KEYS.WELCOME_DIALOG_DISMISSED;

    const updateLocalStorageState = () => {
      setLocalStorageState({
        welcomeHidden: localStorage.getItem(welcomeKey) === "hide",
        notificationHidden: localStorage.getItem(bannerId) === "hide"
      });
    };

    // Initial read from localStorage
    updateLocalStorageState();

    // Listen for banner-toggle events to update state
    const handleBannerToggle = () => {
      updateLocalStorageState();
    };

    window.addEventListener(BANNER_TOGGLE_EVENT, handleBannerToggle);
    return () => window.removeEventListener(BANNER_TOGGLE_EVENT, handleBannerToggle);
  }, []);

  return (
    <React.Fragment>
      {contactState.backgroundProcess ? (
        <Loading message={contactState.backgroundProcessMessage} />
      ) : (
        <>
          <GlobalStyles
            styles={{ ul: { margin: 0, padding: 0, listStyle: "none" } }}
          />
          <CssBaseline />
          <Paper
            square
            sx={{
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${Banner})`,
              height: 400,
            }}
          >
            <Container>
                <Grid
                  item
                  md={8}
                  xs={12}
                  sx={{
                    mt: 8.2,
                    py: { md: 10 },
                  }}
                >
                  <Typography
                    sx={{
                      mt: { md: 1, xs: 2 },
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 700
                    }}
                    component="h1"
                    align="left"
                    color="common.white"
                  >
                    Certification Portal
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      mb: 0.5,
                      fontSize: { xs: '1.25rem', md: '1.75rem' },
                      fontWeight: 600
                    }}
                    component="h2"
                    align="left"
                    color="common.white"
                  >
                    Welcome to the WSO2 Certification Portal.
                  </Typography>
                  <Typography
                    align="left"
                    color="common.white"
                    component="p"
                    sx={{
                      mt: 2,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      fontWeight: 400
                    }}
                  >
                    You can take your certification exams<br />
                    and view or download your certificates.
                  </Typography>
                </Grid>
            </Container>
          </Paper>

          {!auth.isAuthenticated ? <CertificationVerifier /> : null}

          <GuideCards />
          {contactState.state === BannerState.SUCCESS && (
            <WelcomeCard />
          )}
          {auth.isAuthenticated ? (
            <NotificationBannerCard
              initialOpen={Boolean(
                appMaintenanceStatus.isShowMaintenanceBanner &&
                  appMaintenanceStatus.maintenanceBannerContent
              )}
            />
          ) : (
            <PublicNotificationBannerCard />
          )}
          {/* Shared FAB controller to avoid overlapping FABs from individual cards */}
          <BannerFabController
            localStorageState={localStorageState}
            contactState={contactState}
            appMaintenanceStatus={appMaintenanceStatus}
          />
        </>
      )}
    </React.Fragment>
  );
}

export default LandingPage;
